import type { ReactElement } from 'react';

import { patientInitials } from '@/api/mappers';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { cn } from '@/lib/utils';

type PatientInitialsAvatarSize = 'sm' | 'md' | 'lg';

interface PatientInitialsAvatarProps {
  readonly firstName: string | null;
  readonly lastName: string | null;
  readonly size?: PatientInitialsAvatarSize;
  readonly className?: string;
}

/**
 * Square initials avatar for a patient-shaped record. Centralises the
 * `bg-brand` + `text-brand-foreground` chrome so every patient-facing
 * avatar (header, list card, quick-search row) looks identical and stays
 * themeable via the brand token family.
 *
 * Initials are derived from first + last name through `patientInitials`
 * so the fallback stays consistent with other shell affordances.
 */
export function PatientInitialsAvatar({
  firstName,
  lastName,
  size = 'md',
  className,
}: PatientInitialsAvatarProps): ReactElement {
  const initials = patientInitials(firstName, lastName);
  return (
    <Avatar className={cn(SIZE[size].avatar, className)}>
      <AvatarFallback
        className={cn(
          'bg-brand text-brand-foreground font-bold',
          SIZE[size].text,
        )}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
}

const SIZE: Record<
  PatientInitialsAvatarSize,
  { avatar: string; text: string }
> = {
  sm: { avatar: 'h-8 w-8', text: 'text-xs' },
  md: { avatar: 'h-10 w-10', text: 'text-sm' },
  lg: { avatar: 'h-12 w-12', text: 'text-sm' },
};
